import { usePrint } from "routes/context/printContext"
import { useApiAccess } from "features/session/context"
import { KojinDownloadParams } from "features/kojin/dialog/download"
import { KojinPptxFormatList } from "features/kojin/report/pptx"
import { KojinPrintFormatList } from "features/kojin/report/print"
import { KojinResult } from "interface/kojin"
import { SearchDetailResult } from "interface/search"
import { useCallback, useMemo, useState } from "react"

type useKojinPrintProps = {
	search: SearchDetailResult | null
	kojin: KojinResult | null
  onDownloadEnd: () => void
}
export const useKojinPrint = ({
	search,
	kojin,
  onDownloadEnd
}: useKojinPrintProps) => {
  const apiAccess = useApiAccess()
  const print = usePrint()
  const [downloading, setDownloading] = useState<boolean>(false)

	const doPrint = useCallback(
    async ({
      reportId
    }: KojinDownloadParams) => {
      if (!kojin || !reportId || !search || !print) return false

      const formatter = KojinPrintFormatList.find((r) => r.reportId === reportId)
      if (!formatter) return false

      const preProcessData = []
      const reportData = await formatter.preProcess({ apiAccess, search, kojin })
      if (reportData) preProcessData.push(reportData)

      print.doPrint(
        <formatter.record
          key={`print-data`}
          search={search}
          kojin={kojin}
          preProcessData={preProcessData}
          counter={0}
          count={1}
        />
      )
      return true
    },
    [apiAccess, search, kojin, print]
  )

  const doDownloadPptx = useCallback(
    async ({
      reportId,
      title
    }: KojinDownloadParams) => {
      if (!kojin || !reportId || !search) return false

      const formatter = KojinPptxFormatList.find((r) => r.reportId === reportId)
      if (!formatter) return false

      return await formatter.record(
        title, { apiAccess, search, kojinList: [kojin] }
      )
    },
    [apiAccess, search, kojin]
  )

  const onDownload = useCallback(
    async (params: KojinDownloadParams) => {
      setDownloading(true)
      if (params.format === 'report') {
        if (!await doPrint(params)) {
          onDownloadEnd()
          setDownloading(false)
          return
        }
      } else if (params.format === 'pptx') {
        if (!await doDownloadPptx(params)) {
          onDownloadEnd()
          setDownloading(false)
          return
        }
      }
      onDownloadEnd()
      setDownloading(false)
    },
    [onDownloadEnd, doPrint, doDownloadPptx, setDownloading]
  )
  return useMemo(() => ({
    onDownload,
    downloading 
  }), [
    onDownload,
    downloading
  ])
}